import Driver from 'driver.js';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import i18n, { i18n_t } from '@/locales/i18n';
import ipc from '@/renderer/scripts/utils/ipc';
import '@/renderer/assets/custom.driver.scss';

// 默认的option选项
const defaultDriverOption = {
  padding: 0,
  opacity: 0.2,
  allowClose: false,
  // overlayClickNext: true, //是否允许点击蒙层跳转下一步
  showButtons: false, // Do not show control buttons in footer
  // options: { scrollIntoViewOptions: { block: 'start', inline: 'nearest' } },
};
// 轻量化弹窗option
const singleDriverOption = {
  padding: 0,
  opacity: 0,
  allowClose: false,
  showButtons: false,
};
class DriverFactory {
  private driver: Driver;
  constructor(isSearch: boolean) {
    if (isSearch) {
      this.driver = new Driver(singleDriverOption);
    } else {
      this.driver = new Driver(defaultDriverOption);
    }
  }
  start(index?: number) {
    // 打开后默认展示个人空间
    reporter.report('newhand', {
      newhand_guide_show: 'personalSpace',
    });
    this.driver.start(index);
  }
  defineSteps(stepOptions: Array<Driver.Step>) {
    this.driver.defineSteps(stepOptions);
  }
  highlight(element: string, description: string) {
    const d = {
      element,
      popover: {
        // 也可以使用HTML标签
        title: '<div style="display: none">',
        className: 'guide-workbench top-more',
        description,
        position: 'bottom-left',
      },
    };
    this.driver.highlight(d);
  }
  goStop(targetId?: string) {
    targetId &&
      reporter.report('newhand', {
        newhand_guide_skip: targetId,
      });
    this.driver.reset(true);
    ipc.emit(ipc.RENDERER_EVENTS_MAP.STOP_GUIDE, targetId);
    ipc.emit(ipc.RENDERER_EVENTS_MAP.STOP_ADMIN_GUIDE, targetId);
  }
  goNext(targetId: string, nextId?: string, text?: string) {
    targetId &&
      reporter.report('newhand', {
        newhand_guide_clilck: targetId,
      });
    nextId &&
      nextId !== 'smh-guide' &&
      reporter.report('newhand', {
        newhand_guide_show: nextId,
      });
    if (nextId) {
      const target = document.getElementById(nextId);
      try {
        // 兼容浏览器不支持scrollIntoViewIfNeeded的使用
        // @ts-ignore
        if (target && target.scrollIntoViewIfNeeded) {
          // @ts-ignore
          target && target.scrollIntoViewIfNeeded(true);
        } else {
          target && target.scrollIntoView(true);
        }
      } catch (error) {
        logger.ui.error('scrollIntoViewIfNeeded', error);
      }
    }
    if (text === i18n_t('好的') || text === i18n_t('完成')) {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.STOP_GUIDE);
    }
    this.driver.moveNext();
  }

  // eslint-disable-next-line class-methods-use-this
  goAdmin(isAdminPage?: boolean) {
    this.driver.reset(true);
    if (isAdminPage) {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.START_ADMIN_GUIDE, true);
    } else {
      dispatchRouter({
        name: 'spaceStatistics',
        params: {
          guide: 'true',
        },
      });
    }
  }

  searchGuideStop() {
    this.driver.reset(true);
  }
}
export interface Args {
  id: string;
  title: string;
  context: string;
  type?: string;
  position?: string;
  className?: string;
  isAdminPage?: boolean; // 是否是管理员页面
}

/**
 * 新手引导蒙版
 * 相关处理
 * @constructor
 */ // 开启引导步骤
export const startGuideSteps = (stepArguments: Array<Args>, isSearch = false) => {
  // menu侧 字符串模板
  const createTemplateMenu = (
    stepNumber: number,
    length: number,
    id: string,
    title: string,
    context: string,
    nextId?: string,
  ) => {
    const btnText = stepNumber === length ? i18n_t('好的') : i18n_t('下一步');
    const skipText = i18n_t('跳过');
    return `<div class="guide">
      <div class="guide-left">
        <div class="bg-img ${id}"></div>
      </div>
      <div class="guide-right">
        <div>
          <h2 class="guide-right-title">${title}</h2>
          <p class="guide-right-detail">${context}</p>
        </div>
        <div class="guide-right-buttons">
          <button class="t-button gray-1" onclick="driver.goStop('${id}')">${skipText}(${stepNumber}/${
      length - 1
    })</button>
          <button class="t-button primary-1" onclick="driver.goNext('${id}', '${nextId}','${btnText}')">${btnText}</button>
        </div>
      </div>
    </div>`;
  };

  const text = i18n_t('我知道了');
  const createTemplateRightGuide = (id: string, title: string, context: string) => `<div class="guide">
  <div class="guide-left">
    <div class="bg-img ${id}"></div>
  </div>
  <div class="guide-right">
    <div>
      <h2 class="guide-right-title">${title}</h2>
      <p class="guide-right-detail">${context}</p>
    </div>
    <div class="guide-right-buttons">
      <button class="t-button primary-1" onclick="driver.goStop('${id}')">${text}</button>
    </div>
  </div>
</div>`;

  // 进入管理员界面的模板
  const createTemplateGoAdmin = (title: string, context: string, isAdminPage?: boolean) => {
    const btnText = i18n_t('下一步');
    const skipText = i18n_t('跳过');
    return `<div class="guide">
      <div class="guide-left guide-admin">
        <div class="bg-img-admin advanceGuide-step-4"></div>
      </div>
      <div class="guide-right">
        <div>
          <h2 class="guide-right-title">${title}</h2>
          <p class="guide-right-detail">${context}</p>
        </div>
        <div class="guide-right-buttons">
          <button class="t-button gray-1" onclick="driver.goStop()">${skipText}(1/4)</button>
          <button class="t-button primary-1" onclick="driver.goAdmin(${isAdminPage})">${btnText}</button>
        </div>
      </div>
    </div>`;
  };
  // 管理中心的新手引导模板
  const createTemplateAdmin = (
    stepNumber: number,
    length: number,
    id: string,
    title: string,
    context: string,
    nextId?: string,
  ) => {
    const btnText = stepNumber === length ? i18n_t('完成') : i18n_t('下一步');
    const skipText = i18n_t('跳过');
    return `<div class="guide">
      <div class="guide-left guide-admin">
        <div class="bg-img-admin ${id}"></div>
      </div>
      <div class="guide-right">
        <div>
          <h2 class="guide-right-title">${title}</h2>
          <p class="guide-right-detail">${context}</p>
        </div>
        <div class="guide-right-buttons">
          <button class="t-button gray-1" onclick="driver.goStop('${id}')">${skipText}(${stepNumber}/${length})</button>
          <button class="t-button primary-1" onclick="driver.goNext('${id}', '${nextId}','${btnText}')">${btnText}</button>
        </div>
      </div>
    </div>`;
  };
  // 弹窗方式 字符串模板
  const createTemplateWorkbench = (title: string, context: string) => {
    const btnText = i18n_t('好的');
    return `
      <h2 class="guide-workbench-title">${title}</h2>
      <p class="guide-workbench-detail">${context}</p>
      <button class="t-button t-size-m t-button--variant-base t-button--theme-primary"
        onclick="driver.goStop()">${btnText}</button>
        <div class="guide-workbench-img">
    `;
  };
  // 搜索页面新手引导
  const createTemplateGuide = (id: string, title: string, context: string) => {
    const text = i18n_t('我知道了');
    return `<div class="guide">
      <div class="guide-left">
        <div class="bg-img ${id}"></div>
      </div>
      <div class="guide-right-search">
        <div>
          <h2 class="guide-right-title">${title}</h2>
          <p class="guide-right-detail">${context}</p>
        </div>
        <div class="guide-right-buttons">
          <button class="t-button primary-1" onclick="driver.searchGuideStop()">${text}</button>
        </div>
      </div>
    </div>`;
  };
  const driver = new DriverFactory(isSearch);
  // @ts-ignore
  window.driver = driver;
  const getDescription = (item: Args, index: number) => {
    let str = '';
    switch (item?.type) {
      case 'Workbench':
        str = createTemplateWorkbench(item.title, item.context);
        break;
      case 'goAdmin':
        str = createTemplateGoAdmin(item.title, item.context, item.isAdminPage);
        break;
      case 'admin':
        str = createTemplateAdmin(
          index + 2,
          stepArguments.length + 1,
          item.id,
          item.title,
          item.context,
          stepArguments[index + 1]?.id,
        );

        break;
      case 'guideRight':
        str = createTemplateRightGuide(item.id, item.title, item.context);
        break;
      case 'search':
        str = createTemplateGuide(item.id, item.title, item.context);
        break;
      default:
        str = createTemplateMenu(
          index + 1,
          stepArguments.length,
          item.id,
          item.title,
          item.context,
          stepArguments[index + 1]?.id,
        );

        break;
    }

    return str;
  };
  // 设置介绍的步骤
  const stepOptions = stepArguments.map((item: Args, index: number) => ({
    element: `#${item.id}`,
    popover: {
      title: '<div style="display: none">',
      // 也可以使用HTML标签
      className: item?.className || 'right-more',
      description: getDescription(item, index),
      position: item?.position || 'right-center',
    },
  }));
  driver.defineSteps(stepOptions);
  // 开始前先定位第一个
  const target = document.getElementById(stepArguments[0].id);
  try {
    // 兼容浏览器不支持scrollIntoViewIfNeeded的使用
    // @ts-ignore
    if (target && target.scrollIntoViewIfNeeded) {
      // @ts-ignore
      target && target.scrollIntoViewIfNeeded(true);
    } else {
      target && target.scrollIntoView(true);
    }
  } catch (error) {
    logger.ui.error('scrollIntoViewIfNeeded', error);
  }
  // 开始分步引导介绍
  driver.start();
  if (isSearch) {
    ipc.on(ipc.RENDERER_EVENTS_MAP.STOP_SEARCH_GUIDE, () => {
      driver.searchGuideStop();
    });
  }
  return driver;
};
